import styled from "styled-components";
import { createShouldForwardPropBlockList, deepMerge } from "../utils/helper";
import { type ModalProps } from "./modal";
import { getBodyHeight, getBorderRadius, getContentHeight, getContentWidth, getPageHeight, getPosition } from "./modal.styles.utils";

type WrapperProps = Pick<ModalProps, "open" | "padding" | "rounded" | "animation" | "lockScreen">;
type ContainerInnerProps = Pick<ModalProps, "height" | "margin">;
type ContainerProps = Pick<ModalProps, "margin">;
type ContentProps = Pick<ModalProps, "width" | "animation" | "margin" | "transparent">;
type HeaderProps = Pick<ModalProps, "flat"> & {
    custom?: boolean;
};
type PageProps = Pick<ModalProps, "height">;
type BodyProps = Pick<ModalProps, "height">;
type FooterProps = Pick<ModalProps, "flat"> & {
    custom?: boolean;
};

export const Wrapper = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["open", "padding", "rounded", "animation", "lockScreen"]),
})<WrapperProps>(({ open, padding, rounded, animation, lockScreen, theme }) => ({
    color: theme.colors.gray[950],
    ...deepMerge(getPosition(open, lockScreen)),
    display: "flex",
    alignItems: "center",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    zIndex: open ? 1000 : 998,
    pointerEvents: open ? "auto" : "none",
    [`${Content}`]: {
        ...deepMerge(getBorderRadius(rounded, theme)),
        ...(open && { opacity: 1, scale: "100%" }),
        ...(open && animation !== "smooth" && { transitionTimingFunction: theme.transitionTimingFunction["out-back"] }),
        ...(open && animation === "smooth" && { translate: "0 0" }),
    },
    [`${Background}`]: {
        ...(open && { opacity: 0.5 }),
    },
    [`${Body}`]: {
        ...(padding && { padding: theme.spacing[6] }),
    },
    [`${Sticky}`]: {
        ...(padding && { paddingLeft: theme.spacing[6], paddingRight: theme.spacing[6] }),
    },
}));

export const Container = styled.div.withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["margin"]),
})<ContainerProps>(({ margin, theme }) => ({
    zIndex: 2,
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    ...(margin && { paddingTop: theme.spacing[5], paddingBottom: theme.spacing[5] }),
}));

export const ContainerInner = styled.div.withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["height", "margin"]),
})<ContainerInnerProps>(({ height, margin, theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    ...deepMerge(getContentHeight(height)),
    ...(margin && { paddingLeft: theme.spacing[5], paddingRight: theme.spacing[5] }),
}));

export const Content = styled.div.withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["width", "animation", "margin", "transparent"]),
})<ContentProps>(({ width, animation, margin, transparent, theme }) => ({
    ...(!transparent && { backgroundColor: theme.colors.white }),
    ...deepMerge(getContentWidth(width)),
    position: "relative",
    width: "100%",
    boxShadow: theme.boxShadow["box-light"],
    opacity: 0,
    transitionProperty: "translate, scale, opacity",
    transitionDuration: "300ms",
    transformOrigin: "top",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    ...(animation === "smooth" && { translate: `0 ${theme.spacing[2]}` }),
    ...(animation !== "smooth" && { scale: "90%" }),
    ...(margin && { marginLeft: theme.spacing[6], marginRight: theme.spacing[6] }),
}));

export const Close = styled("div")(({ theme }) => ({
    position: "absolute",
    right: theme.spacing[6],
    top: theme.spacing[6],
    zIndex: 10,
}));

export const Pages = styled("div")(() => ({
    display: "flex",
    alignItems: "flex-start",
    height: "100%",
}));

export const Page = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["height"]),
})<PageProps>(({ height }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    ...deepMerge(getPageHeight(height)),
}));

export const Header = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["custom", "flat"]),
})<HeaderProps>(({ custom, flat, theme }) => ({
    ...(!custom && {
        padding: theme.spacing[6],
        borderBottomWidth: theme.borderWidth.DEFAULT,
        borderStyle: "solid",
        borderBottomColor: theme.colors.gray[100],
    }),
    ...(flat && {
        paddingBottom: 0,
        borderBottom: "none",
    }),
}));

export const Title = styled("div")(({ theme }) => ({
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.medium,
}));

export const Subtitle = styled("div")(({ theme }) => ({
    color: theme.colors.gray[500],
    fontSize: theme.fontSize.sm,
}));

export const Sticky = styled("div")(({ theme }) => ({
    borderBottomColor: theme.colors.gray[100],
    borderBottomWidth: theme.borderWidth.DEFAULT,
    borderStyle: "solid",
}));

export const Body = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["height"]),
})<BodyProps>(({ height }) => ({
    overflowY: "auto",
    overflowX: "hidden",
    ...deepMerge(getBodyHeight(height)),
}));

export const Footer = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["custom", "flat"]),
})<FooterProps>(({ custom, flat, theme }) => ({
    ...(!custom && {
        paddingLeft: theme.spacing[6],
        paddingRight: theme.spacing[6],
        paddingTop: flat ? 0 : theme.spacing[4],
        paddingBottom: theme.spacing[4],
        borderTopWidth: theme.borderWidth.DEFAULT,
        borderStyle: "solid",
        borderTopColor: theme.colors.gray[100],
        display: "flex",
        alignItems: "center",
        gap: theme.spacing[2],
        justifyContent: "flex-end",
    }),
    ...(flat && {
        borderTop: "none",
    }),
}));

export const Background = styled("div")(({ theme }) => ({
    zIndex: 1,
    backgroundColor: theme.colors.gray[950],
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    transitionProperty: "opacity",
    transitionDuration: "300ms",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
}));

export const LoadingWrapper = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));
